import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchMe, login as loginRequest, logout as logoutRequest, type Account } from '../api/auth';
import { ApiError } from '../api/client';
import { AuthContext, type AuthState } from './AuthContext';

/**
 * Holds the signed-in account for the app.
 *
 * The session itself lives in an HttpOnly cookie the browser manages; this is
 * only a cache of who the backend says we are. Nothing here grants access —
 * every request is authorised server-side (Rules 7, 8).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setAccount(await fetchMe());
    } catch (error) {
      // 401 simply means "not signed in" — it is not a failure to report.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Could not load the current session', error);
      }
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const signedIn = await loginRequest(email, password);
    setAccount(signedIn);
    return signedIn;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Whatever the server said, this browser is done with the session.
      setAccount(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ account, isLoading, signIn, signOut, setAccount, refresh }),
    [account, isLoading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
